/**
 * Board Editor Page
 * 
 * Allows caregivers to select which board to edit.
 * Displays options as horizontal strips with edit buttons.
 * 
 * @module BoardEditor
 */

import React from "react";
import { useNavigate } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import { useCaregiverTranslation } from "@/hooks/useCaregiverTranslation";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

interface BoardOption {
  id: string;
  labelHe: string;
  labelEn: string;
  href: string;
}

// Order: Home, Help, Communication, Keyboard, Entertainment
const boardOptions: BoardOption[] = [
  {
    id: "home",
    labelHe: "עריכת מסך הבית",
    labelEn: "Edit Home Screen",
    href: "/home-editor",
  },
  {
    id: "help",
    labelHe: "עריכת לוח עזרה",
    labelEn: "Edit Help Board",
    href: "/emergency?edit=true",
  },
  {
    id: "communication",
    labelHe: "עריכת לוח תקשורת",
    labelEn: "Edit Talker Board",
    href: "/communication?edit=true",
  },
  {
    id: "keyboard",
    labelHe: "עריכת מקלדת",
    labelEn: "Edit Keyboard",
    href: "/keyboard-editor",
  },
  {
    id: "entertainment",
    labelHe: "עריכת בידור",
    labelEn: "Edit Media",
    href: "/entertainment-editor",
  },
];

export default function BoardEditor() {
  const navigate = useNavigate();
  const { dir, isRTL } = useCaregiverTranslation();

  return (
    <MainLayout onBack={() => navigate(-1)}>
      <div className="flex-1 flex flex-col min-h-0" dir={dir}>
        {/* Header - matches Settings page style */}
        <header className="flex-shrink-0 flex items-center gap-3 px-6 py-4 border-b border-primary bg-card/50">
          <h1 className="text-2xl font-bold">
            {isRTL ? "עריכת לוחות" : "Edit Boards"}
          </h1>
        </header>

        {/* Strips container */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6">
          <div className="flex flex-col gap-3 max-w-2xl mx-auto">
            {boardOptions.map((board) => (
              <Card
                key={board.id}
                className={cn(
                  "flex items-center justify-between p-4 cursor-pointer",
                  "border border-primary bg-card shadow-none",
                  "hover:bg-primary/10 transition-colors"
                )}
                onClick={() => navigate(board.href)}
              >
                <span className="text-lg font-medium text-foreground">
                  {isRTL ? board.labelHe : board.labelEn}
                </span>
                
                <Button
                  size="icon"
                  variant="ghost"
                  className="shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(board.href);
                  }}
                >
                  <Pencil className="w-5 h-5" />
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </MainLayout>
  );
}